import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { VignetteShader } from 'three/examples/jsm/shaders/VignetteShader.js';
import type { RenderQuality } from '../RenderQuality';

export interface PostFxConfig {
  vignette: boolean;
  motionBlur: boolean;
  fpvDistortion: boolean;
  motionBlurStrengthAt(speedMps: number): number;
}

export type PostFxComposer = EffectComposer & {
  /** Updates the velocity-scaled blur without rebuilding the composer. */
  setSpeedMps(speedMps: number): void;
};

const MAX_MOTION_BLUR_STRENGTH = 0.32;
const MOTION_BLUR_FULL_SPEED_MPS = 40;

const motionBlurStrengthAt = (speedMps: number): number => {
  const speed = Number.isFinite(speedMps) ? Math.max(0, speedMps) : 0;
  return Math.min(MAX_MOTION_BLUR_STRENGTH, (speed / MOTION_BLUR_FULL_SPEED_MPS) * 0.24);
};

export const postFxConfigFor = (quality: RenderQuality): PostFxConfig => {
  const vignette = quality !== 'low';
  const motionBlur = quality === 'high';
  const fpvDistortion = quality !== 'low';
  return {
    vignette,
    motionBlur,
    fpvDistortion,
    motionBlurStrengthAt,
  };
};

const MotionBlurShader = {
  uniforms: {
    tDiffuse: { value: null },
    strength: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float strength;
    varying vec2 vUv;
    void main() {
      vec2 direction = vec2(strength * 0.018, 0.0);
      vec4 color = texture2D(tDiffuse, vUv) * 0.4;
      color += texture2D(tDiffuse, vUv - direction) * 0.15;
      color += texture2D(tDiffuse, vUv + direction) * 0.15;
      color += texture2D(tDiffuse, vUv - direction * 2.0) * 0.15;
      color += texture2D(tDiffuse, vUv + direction * 2.0) * 0.15;
      gl_FragColor = color;
    }
  `,
};

const FpvDistortionShader = {
  uniforms: {
    tDiffuse: { value: null },
    distortion: { value: 0.055 },
  },
  vertexShader: MotionBlurShader.vertexShader,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float distortion;
    varying vec2 vUv;
    void main() {
      vec2 centered = vUv * 2.0 - 1.0;
      float radius2 = dot(centered, centered);
      vec2 warpedUv = (centered * (1.0 + distortion * radius2) + 1.0) * 0.5;
      if (warpedUv.x < 0.0 || warpedUv.x > 1.0 || warpedUv.y < 0.0 || warpedUv.y > 1.0) {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
      } else {
        gl_FragColor = texture2D(tDiffuse, warpedUv);
      }
    }
  `,
};

/**
 * Builds browser-only passes when a renderer exists. The early return occurs
 * before EffectComposer construction, keeping Node/headless traversal safe.
 */
export const buildPostFx = (
  renderer: THREE.WebGLRenderer | null | undefined,
  scene: THREE.Scene,
  camera: THREE.Camera,
  config: PostFxConfig,
): PostFxComposer | undefined => {
  if (!renderer) return undefined;

  const composer = new EffectComposer(renderer) as PostFxComposer;
  composer.addPass(new RenderPass(scene, camera));

  let blurPass: ShaderPass | undefined;
  if (config.motionBlur) {
    blurPass = new ShaderPass(MotionBlurShader);
    composer.addPass(blurPass);
  }
  if (config.fpvDistortion) composer.addPass(new ShaderPass(FpvDistortionShader));
  if (config.vignette) composer.addPass(new ShaderPass(VignetteShader));

  composer.setSpeedMps = (speedMps: number): void => {
    if (blurPass) blurPass.uniforms.strength!.value = config.motionBlurStrengthAt(speedMps);
  };
  return composer;
};
